import { Badge } from '@/components/ui/badge';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { z } from 'zod';
import { FormSection, MasterDataForm } from '../components/master-data/MasterDataForm';
import { FilterConfig, MasterDataTable, TableColumn } from '../components/master-data/MasterDataTable';
import { useAuth } from '../hooks/useAuth';
import { useToast } from '../hooks/useToast';

interface Product {
  _id: string;
  name: string;
  code: string;
  productFormat: 'Dox' | 'Non Dox' | 'Envelope' | 'Commercial' | 'OTHERS';
  hideInPortal: boolean;
  isCsbvShipment: boolean;
  isCommercial: boolean;
  showForUsLocalOnly: boolean;
  status: 'Active' | 'Inactive';
  createdAt: string;
  updatedAt: string;
  createdBy?: string;
  updatedBy?: string;
}

const productFormats = [
  { value: 'Dox', label: 'Dox' },
  { value: 'Non Dox', label: 'Non Dox' },
  { value: 'Envelope', label: 'Envelope' },
  { value: 'Commercial', label: 'Commercial' },
  { value: 'OTHERS', label: 'Others' },
];

const statusOptions = [
  { value: 'Active', label: 'Active' },
  { value: 'Inactive', label: 'Inactive' },
];

const ProductManagement = () => {
  const queryClient = useQueryClient();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [serverErrors, setServerErrors] = useState<Record<string, string>>({});
  const { axios } = useAuth();
  const { toast } = useToast();

  // State for server-side filtering and pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentFilters, setCurrentFilters] = useState<Record<string, string>>({});
  const [sortConfig, setSortConfig] = useState<{ sortBy?: string; sortOrder?: 'asc' | 'desc' }>({});

  // Fetch products with server-side filtering and pagination
  const { data: productsResponse, isLoading } = useQuery({
    queryKey: ['products', currentPage, pageSize, searchTerm, currentFilters, sortConfig],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.append('page', currentPage.toString());
      params.append('limit', pageSize.toString());
      
      if (searchTerm) {
        params.append('search', searchTerm);
      }
      
      // Add filters
      Object.entries(currentFilters).forEach(([key, value]) => {
        if (value && value !== 'all') {
          params.append(key, value);
        }
      });
      
      // Add sorting
      if (sortConfig.sortBy) {
        params.append('sortBy', sortConfig.sortBy);
        params.append('sortOrder', sortConfig.sortOrder || 'asc');
      }

      const response = await axios.get(`/api/master-data/product?${params.toString()}`);
      const result = response.data.data || {};
      console.log('Product API response:', result);
      
      return {
        data: result.data || [],
        pagination: {
          total: result.total || 0,
          page: result.page || 1,
          limit: result.limit || 10,
          totalPages: result.totalPages || 1
        }
      };
    },
  });

  const products = productsResponse?.data || [];
  const pagination = productsResponse?.pagination;

  // Create product mutation
  const createProductMutation = useMutation({
    mutationFn: async (data: any) => {
      const response = await axios.post('/api/master-data/product', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      queryClient.invalidateQueries({ queryKey: ['master-data-count', 'product'] });
      toast({
        title: 'Product created',
        description: 'Product has been created successfully.',
      });
      setIsFormOpen(false);
      setEditingProduct(null);
      setServerErrors({});
    },
    onError: (error: any) => {
      // Extract validation errors if available
      const validationErrors = error.response?.data?.errors || {};
      if (Object.keys(validationErrors).length > 0) {
        setServerErrors(validationErrors);
      } else {
        setServerErrors({});
        toast({
          title: 'Error creating product',
          description: error.response?.data?.error?.message || error.response?.data?.error || 'Failed to create product',
          variant: 'destructive',
        });
      }
    },
  });

  // Update product mutation
  const updateProductMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: any }) => {
      const response = await axios.put(`/api/master-data/product/${id}`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      queryClient.invalidateQueries({ queryKey: ['master-data-count', 'product'] });
      toast({
        title: 'Product updated',
        description: 'Product has been updated successfully.',
      });
      setIsFormOpen(false);
      setEditingProduct(null);
      setServerErrors({});
    },
    onError: (error: any) => {
      // Extract validation errors if available
      const validationErrors = error.response?.data?.errors || {};
      if (Object.keys(validationErrors).length > 0) {
        setServerErrors(validationErrors);
      } else {
        setServerErrors({});
        toast({
          title: 'Error updating product',
          description: error.response?.data?.error?.message || error.response?.data?.error || 'Failed to update product',
          variant: 'destructive',
        });
      }
    },
  });

  // Delete product mutation (hard delete)
  const deleteProductMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await axios.delete(`/api/master-data/product/${id}?soft=false`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      queryClient.invalidateQueries({ queryKey: ['master-data-count', 'product'] });
      toast({
        title: 'Product deleted',
        description: 'Product has been deleted successfully.',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Error deleting product',
        description: error.response?.data?.error || 'Failed to delete product',
        variant: 'destructive',
      });
    },
  });

  // Table columns configuration with better responsive design
  const columns: TableColumn<Product>[] = [
    {
      key: 'name',
      label: 'Product Name',
      sortable: true,
      render: (value, product) => (
        <div className="space-y-1">
          <div className="font-medium text-sm">
            {product.name}
          </div>
          <div className="text-xs text-muted-foreground font-mono">
            {product.code}
          </div>
        </div>
      ),
    },
    {
      key: 'code',
      label: 'Code',
      sortable: true,
      hideOnTablet: true,
      render: (value, product) => (
        <div className="font-mono text-sm bg-muted px-2 py-1 rounded">
          {product.code}
        </div>
      ),
    },
    {
      key: 'productFormat',
      label: 'Format',
      sortable: true,
      render: (value, product) => (
        <Badge 
          variant="outline"
          className="text-xs font-medium"
        >
          {product.productFormat}
        </Badge>
      ),
    },
    {
      key: 'isCommercial',
      label: 'Commercial',
      hideOnTablet: true,
      render: (value, product) => (
        <Badge 
          variant={product.isCommercial ? 'default' : 'secondary'}
          className={product.isCommercial 
            ? 'bg-blue-100 text-blue-800 hover:bg-blue-100' 
            : 'bg-gray-100 text-gray-800 hover:bg-gray-100'
          }
        >
          {product.isCommercial ? 'Yes' : 'No'}
        </Badge>
      ),
    },
    {
      key: 'isCsbvShipment',
      label: 'CSBV',
      hideOnTablet: true,
      render: (value, product) => (
        <Badge 
          variant={product.isCsbvShipment ? 'default' : 'secondary'}
          className={product.isCsbvShipment 
            ? 'bg-purple-100 text-purple-800 hover:bg-purple-100' 
            : 'bg-gray-100 text-gray-800 hover:bg-gray-100'
          }
        >
          {product.isCsbvShipment ? 'Yes' : 'No'}
        </Badge>
      ),
    },
    {
      key: 'hideInPortal',
      label: 'Portal Visibility',
      hideOnTablet: true,
      render: (value, product) => (
        <Badge 
          variant={product.hideInPortal ? 'destructive' : 'default'}
          className={product.hideInPortal 
            ? 'bg-red-100 text-red-800 hover:bg-red-100' 
            : 'bg-green-100 text-green-800 hover:bg-green-100'
          }
        >
          {product.hideInPortal ? 'Hidden' : 'Visible'}
        </Badge>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: (value, product) => (
        <Badge 
          variant={product.status === 'Active' ? 'default' : 'secondary'}
          className={product.status === 'Active' 
            ? 'bg-green-100 text-green-800 hover:bg-green-100' 
            : 'bg-red-100 text-red-800 hover:bg-red-100'
          }
        >
          {product.status}
        </Badge>
      ),
    },
    {
      key: 'createdAt',
      label: 'Created',
      sortable: true,
      hideOnTablet: true,
      render: (value, product) => (
        <div className="text-sm text-muted-foreground">
          {new Date(product.createdAt).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
          })}
        </div>
      ),
    },
  ];

  // Filter configuration with improved options
  const filterConfig: FilterConfig[] = [
    {
      key: 'status',
      label: 'Status',
      type: 'select',
      placeholder: 'Filter by status',
      options: [
        { value: 'all', label: 'All Statuses' },
        { value: 'Active', label: 'Active Products' },
        { value: 'Inactive', label: 'Inactive Products' },
      ],
    },
    {
      key: 'productFormat',
      label: 'Product Format',
      type: 'select',
      placeholder: 'Filter by format',
      options: [
        { value: 'all', label: 'All Formats' },
        ...productFormats,
      ],
    },
    {
      key: 'isCommercial',
      label: 'Commercial Type',
      type: 'select',
      placeholder: 'Filter by commercial type',
      options: [
        { value: 'all', label: 'All Types' },
        { value: 'true', label: 'Commercial Products' },
        { value: 'false', label: 'Non-Commercial Products' },
      ],
    },
    {
      key: 'isCsbvShipment',
      label: 'CSBV Shipment',
      type: 'select',
      placeholder: 'Filter by CSBV',
      options: [
        { value: 'all', label: 'All CSBV Types' },
        { value: 'true', label: 'CSBV Shipments' },
        { value: 'false', label: 'Non-CSBV Shipments' },
      ],
    },
    {
      key: 'hideInPortal',
      label: 'Portal Visibility',
      type: 'select',
      placeholder: 'Filter by visibility',
      options: [
        { value: 'all', label: 'All Visibility' },
        { value: 'false', label: 'Visible in Portal' },
        { value: 'true', label: 'Hidden in Portal' },
      ],
    },
    {
      key: 'name',
      label: 'Product Name',
      type: 'text',
      placeholder: 'Search by product name...',
    },
    {
      key: 'code',
      label: 'Product Code',
      type: 'text',
      placeholder: 'Filter by product code...',
    },
  ];

  // Form sections configuration with better layout and validation
  const formSections: FormSection[] = [
    {
      title: 'Product Information',
      description: 'Basic product details and configuration. The product code will be auto-generated if not provided.',
      fields: [
        {
          name: 'name',
          label: 'Product Name',
          type: 'text',
          required: true,
          placeholder: 'Enter product name (e.g., "Express Document Delivery")',
          description: 'A descriptive name for this product offering',
          validation: z.string()
            .min(2, 'Product name must be at least 2 characters')
            .max(200, 'Product name cannot exceed 200 characters')
            .regex(/^[a-zA-Z0-9\s\-_().&]+$/, 'Product name can only contain letters, numbers, spaces, and common symbols'),
        },
        {
          name: 'code',
          label: 'Product Code',
          type: 'text',
          required: false,
          placeholder: 'Leave empty for auto-generation or enter custom code',
          description: 'Unique identifier for this product (auto-generated if not provided)',
          validation: z.string()
            .optional()
            .refine(val => !val || val.length >= 3, 'Product code must be at least 3 characters if provided')
            .refine(val => !val || /^[A-Z0-9_-]+$/.test(val), 'Product code can only contain uppercase letters, numbers, hyphens, and underscores'),
        },
        {
          name: 'productFormat',
          label: 'Product Format',
          type: 'select',
          required: true,
          options: productFormats,
          description: 'Select the format category for this product',
          validation: z.enum(['Dox', 'Non Dox', 'Envelope', 'Commercial', 'OTHERS']),
        },
        {
          name: 'status',
          label: 'Status',
          type: 'radio',
          required: true,
          options: [
            { value: 'Active', label: 'Active - Available for use' },
            { value: 'Inactive', label: 'Inactive - Not available for use' }
          ],
          description: 'Set the availability status for this product',
          validation: z.enum(['Active', 'Inactive']),
        },
      ],
    },
    {
      title: 'Product Settings',
      description: 'Advanced configuration options, visibility settings, and special handling requirements',
      fields: [
        {
          name: 'isCommercial',
          label: 'Commercial Product',
          type: 'checkbox',
          description: 'Check if this product is for commercial shipments',
        },
        {
          name: 'isCsbvShipment',
          label: 'CSBV Shipment',
          type: 'checkbox',
          description: 'Check if this product requires CSBV (Cross-Border Small Volume) handling',
        },
        {
          name: 'hideInPortal',
          label: 'Hide in Customer Portal',
          type: 'checkbox',
          description: 'Check to hide this product from customer portal (admin access only)',
        },
        {
          name: 'showForUsLocalOnly',
          label: 'US Local Customers Only',
          type: 'checkbox',
          description: 'Check to show this product only for US Local customer type',
        },
      ],
    },
  ];

  const handleCreate = () => {
    setEditingProduct(null);
    setServerErrors({});
    setIsFormOpen(true);
  };

  const handleEdit = (product: Product) => {
    // Transform data for form editing
    const transformedProduct = {
      ...product,
      // Ensure boolean values are properly set
      hideInPortal: Boolean(product.hideInPortal),
      isCsbvShipment: Boolean(product.isCsbvShipment),
      isCommercial: Boolean(product.isCommercial),
      showForUsLocalOnly: Boolean(product.showForUsLocalOnly),
    };
    setEditingProduct(transformedProduct);
    setServerErrors({});
    setIsFormOpen(true);
  };

  const handleDelete = (product: Product) => {
    if (window.confirm(`Are you sure you want to permanently delete "${product.name}"? This action cannot be undone.`)) {
      deleteProductMutation.mutate(product._id);
    }
  };

  const handleSubmit = (data: any) => {
    // Transform form data for better API compatibility
    const transformedData = {
      ...data,
      // Convert boolean fields properly
      hideInPortal: Boolean(data.hideInPortal),
      isCsbvShipment: Boolean(data.isCsbvShipment),
      isCommercial: Boolean(data.isCommercial),
      showForUsLocalOnly: Boolean(data.showForUsLocalOnly),
      // Convert string 'Active'/'Inactive' to proper format
      status: data.status || 'Active',
    };

    // Remove empty code field to allow auto-generation
    if (!transformedData.code || transformedData.code.trim() === '') {
      delete transformedData.code;
    }

    if (editingProduct) {
      updateProductMutation.mutate({ id: editingProduct._id, data: transformedData });
    } else {
      createProductMutation.mutate(transformedData);
    }
  };

  const handleFormClose = () => {
    setIsFormOpen(false);
    setEditingProduct(null);
    setServerErrors({});
  };

  const handleRefresh = () => {
    queryClient.invalidateQueries({ queryKey: ['products'] });
  };

  // Export functionality
  const handleExport = async () => {
    try {
      const params = new URLSearchParams();
      
      // Add current filters to export (but not pagination - we want all records)
      if (searchTerm) {
        params.append('search', searchTerm);
      }
      
      Object.entries(currentFilters).forEach(([key, value]) => {
        if (value && value !== 'all') {
          params.append(key, value);
        }
      });

      const response = await axios.get(`/api/master-data/product/export?${params.toString()}`);
      
      if (response.data.success) {
        const data = response.data.data;
        
        // Convert to CSV
        if (data.length > 0) {
          const headers = ['Name', 'Code', 'Format', 'Status', 'Commercial', 'CSBV', 'Hidden in Portal', 'US Local Only', 'Created Date'];
          const csvContent = [
            headers.join(','),
            ...data.map((product: any) => [
              `"${product.name || ''}"`,
              `"${product.code || ''}"`,
              `"${product.productFormat || ''}"`,
              `"${product.status || ''}"`,
              `"${product.isCommercial ? 'Yes' : 'No'}"`,
              `"${product.isCsbvShipment ? 'Yes' : 'No'}"`,
              `"${product.hideInPortal ? 'Yes' : 'No'}"`,
              `"${product.showForUsLocalOnly ? 'Yes' : 'No'}"`,
              `"${new Date(product.createdAt).toLocaleDateString()}"`
            ].join(','))
          ].join('\n');

          // Download CSV
          const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
          const link = document.createElement('a');
          link.href = URL.createObjectURL(blob);
          link.download = `products_export_${new Date().toISOString().split('T')[0]}.csv`;
          link.click();
          
          toast({
            title: 'Export successful',
            description: `Exported ${data.length} product records.`,
          });
        } else {
          toast({
            title: 'No data to export',
            description: 'No products match the current filters.',
            variant: 'destructive',
          });
        }
      }
    } catch (error: any) {
      console.error('Export error:', error);
      toast({
        title: 'Export failed',
        description: error.response?.data?.error || 'Failed to export products',
        variant: 'destructive',
      });
    }
  };

  // Transform product data for form editing
  const transformedEditingProduct = editingProduct ? {
    ...editingProduct,
    // Ensure proper data types for form
    status: editingProduct.status || 'Active',
  } : null;

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-6 max-w-7xl">
        <MasterDataTable
          title="Product Management"
          description="Manage products and their specifications, formats, and configurations"
          data={products}
          columns={columns}
          isLoading={isLoading}
          onAdd={handleCreate}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onRefresh={handleRefresh}
          onExport={handleExport}
          filters={filterConfig}
          searchPlaceholder="Search products by name, code, format, or settings..."
          allowBulkOperations={false}
          allowImportExport={true}
          // Server-side filtering and pagination props
          serverSide={true}
          pagination={pagination}
          onPageChange={setCurrentPage}
          onPageSizeChange={setPageSize}
          onSearchChange={setSearchTerm}
          onFiltersChange={setCurrentFilters}
          onSortChange={setSortConfig}
          currentSearch={searchTerm}
          currentFilters={currentFilters}
          currentSort={sortConfig}
        />

        <MasterDataForm
          isOpen={isFormOpen}
          onClose={handleFormClose}
          onSubmit={handleSubmit}
          sections={formSections}
          title={editingProduct ? 'Edit Product' : 'Add New Product'}
          description={
            editingProduct
              ? 'Update product specifications, settings, and requirements.'
              : 'Create a new product with all specifications and configuration options.'
          }
          isLoading={createProductMutation.isPending || updateProductMutation.isPending}
          initialData={transformedEditingProduct || { 
            status: 'Active', 
            name: '', 
            code: '', 
            productFormat: '',
            hideInPortal: false,
            isCsbvShipment: false,
            isCommercial: false,
            showForUsLocalOnly: false
          }}
          mode={editingProduct ? 'edit' : 'create'}
          serverErrors={serverErrors}
          entityName="Product"
        />
      </div>
    </div>
  );
};

export default ProductManagement; 